import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  ArrowRight, Zap, FileText, Bell, BarChart3, 
  Calculator, Clock, Shield, Hammer, Home, Wrench, PaintBucket, Truck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const SolutionArtisan = () => {
  const features = [
    {
      icon: FileText,
      title: "Devis et factures liés",
      description: "Transformez un devis accepté en facture en 1 clic. Numérotation automatique conforme."
    },
    {
      icon: Calculator,
      title: "Gestion des acomptes",
      description: "Facturez des acomptes (30%, 50%...) et suivez le solde restant dû automatiquement."
    },
    {
      icon: Bell,
      title: "Relances automatiques",
      description: "Vos clients sont relancés automatiquement. Plus de temps perdu à courir après les paiements."
    },
    {
      icon: BarChart3,
      title: "Suivi de chantier",
      description: "Visualisez vos chantiers en cours, facturés et payés. Dashboard clair et simple."
    },
    {
      icon: Clock,
      title: "Facturation sur site",
      description: "Créez vos factures depuis votre téléphone, directement chez le client."
    },
    {
      icon: Shield,
      title: "Conforme 2026",
      description: "Factures Factur-X prêtes pour la réforme. Export pour archivage légal."
    }
  ];

  const trades = [
    { icon: Hammer, label: "Menuisiers" },
    { icon: Home, label: "Maçons" },
    { icon: Wrench, label: "Plombiers" },
    { icon: PaintBucket, label: "Peintres" },
    { icon: Truck, label: "Électriciens" }
  ];

  const faqs = [
    {
      q: "SmartFacture gère-t-il les devis pour artisans ?",
      a: "Oui ! Créez des devis professionnels avec mentions obligatoires (validité, TVA, conditions). Un devis accepté se transforme en facture en 1 clic."
    },
    {
      q: "Comment facturer des acomptes ?",
      a: "SmartFacture permet de facturer des acomptes (30%, 50%...) et génère automatiquement les factures de situation et la facture finale avec le solde."
    },
    {
      q: "Puis-je créer des factures sur chantier ?",
      a: "Oui, l'application est responsive. Créez devis et factures depuis votre smartphone, directement chez le client."
    },
    {
      q: "Les mentions obligatoires pour artisans sont-elles incluses ?",
      a: "Oui : assurance décennale, garantie, conditions de paiement, TVA... Toutes les mentions légales s'ajoutent automatiquement."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Logiciel Facturation Artisan | SmartFacture AI - Devis & Factures BTP</title>
        <meta name="description" content="Logiciel de facturation pour artisans et BTP. Devis, acomptes, factures de situation. Conforme 2026. Essai gratuit 7 jours." />
        <link rel="canonical" href="https://smartfacture-ai.com/solutions/artisan" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/outils/facture-gratuite" className="text-slate-600 hover:text-slate-900 hidden sm:block">
                Outil gratuit
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700">Essai gratuit</Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-slate-700 to-slate-900 text-white py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 mb-6">
                Solution Artisan & BTP
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Facturation simplifiée pour artisans
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Devis, acomptes, factures de situation. Gérez vos chantiers de A à Z sans paperasse. Conforme à la réforme 2026.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto bg-amber-500 text-slate-900 hover:bg-amber-400">
                    Essai gratuit 7 jours
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-slate-500 text-white hover:bg-slate-800">
                    Voir la démo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trades */}
        <section className="py-8 bg-white border-b">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-6">
              {trades.map((t, i) => {
                const Icon = t.icon;
                return (
                  <div key={i} className="flex items-center gap-2 text-slate-600">
                    <Icon className="h-5 w-5 text-amber-500" />
                    <span>{t.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Tout pour gérer vos chantiers
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Du premier devis à la facture finale, SmartFacture vous accompagne sur tous vos projets.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-slate-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                    <p className="text-slate-600">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Workflow */}
        <section className="py-16 bg-amber-50">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Du devis à la facture finale
            </h2>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { step: "1", title: "Devis", desc: "Créez un devis détaillé avec conditions" },
                { step: "2", title: "Acompte", desc: "Facturez 30% à la signature" },
                { step: "3", title: "Situation", desc: "Factures intermédiaires selon avancement" },
                { step: "4", title: "Solde", desc: "Facture finale avec récapitulatif" }
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl p-6 text-center relative">
                  {i < 3 && (
                    <ArrowRight className="absolute -right-2 top-1/2 -translate-y-1/2 h-6 w-6 text-amber-400 hidden md:block" />
                  )}
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-3">
                    {s.step}
                  </div>
                  <h3 className="font-semibold text-slate-900">{s.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-slate-100">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 rounded-2xl p-8 lg:p-12 text-white text-center">
              <Hammer className="h-12 w-12 mx-auto mb-4 text-amber-400" />
              <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                Simplifiez votre facturation
              </h2>
              <p className="text-slate-300 mb-6">
                Rejoignez les artisans qui gagnent du temps avec SmartFacture AI.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-amber-500 text-slate-900 hover:bg-amber-400">
                  Essai gratuit 7 jours
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Questions fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SolutionArtisan;
